import type { BulletSystem } from './BulletSystem';
import { EnemyRole, type EnemySystem } from './EnemySystem';
import type { HomeFieldEnvironment } from './HomeFieldEnvironment';

type SpawnContext = {
  allies?: { deploy: (kind: 'boat', x: number, y: number) => void };
  enemies: EnemySystem;
  bullets: BulletSystem;
  playerX: number;
  playerY: number;
};

type StageEvent = {
  time: number;
  label: string;
  run(context: SpawnContext): void;
};

const events: StageEvent[] = [
  {
    time: 1.2,
    label: 'TICK flock — read the center gap',
    run: ({ enemies }) => {
      for (let index = 0; index < 22; index += 1) {
        enemies.spawn(EnemyRole.Tick, -15.5 + index * 1.45, 27 + Math.abs(10.5 - index) * 0.5);
      }
    },
  },
  {
    time: 6,
    label: 'Furrow lanes — ROUNDEL escort',
    run: ({ enemies }) => {
      for (let index = 0; index < 14; index += 1) {
        const side = index % 2 === 0 ? -1 : 1;
        enemies.spawn(EnemyRole.Tick, side * (5 + (index % 4) * 2.3), 27 + index * 0.55);
      }
      enemies.spawn(EnemyRole.Roundel, 0, 28.5);
    },
  },
  {
    time: 9.5,
    label: 'PICKET lock-and-dive',
    run: ({ enemies, playerX, playerY }) => {
      enemies.spawn(EnemyRole.Picket, -15, 26, playerX - 2, playerY);
      enemies.spawn(EnemyRole.Picket, 15, 27.5, playerX + 2, playerY);
    },
  },
  {
    time: 16,
    label: 'ROUNDEL lance',
    run: ({ enemies }) => {
      enemies.spawn(EnemyRole.Roundel, -9, 27);
      enemies.spawn(EnemyRole.Roundel, 0, 29);
      enemies.spawn(EnemyRole.Roundel, 9, 27);
    },
  },
  {
    time: 22,
    label: 'Alternating acquisition fan',
    run: ({ enemies, playerX, playerY }) => {
      for (let index = 0; index < 5; index += 1) {
        enemies.spawn(EnemyRole.Tick, -12 + index * 6, 27 + index * 0.7);
      }
      enemies.spawn(EnemyRole.Picket, -16, 24, playerX, playerY + 2);
    },
  },
  {
    time: 29,
    label: 'Bridge approach — mixed doctrine',
    run: ({ enemies }) => {
      for (let index = 0; index < 4; index += 1) {
        enemies.spawn(EnemyRole.Roundel, index % 2 === 0 ? -11 : 11, 27 + index * 1.2);
      }
    },
  },
  {
    time: 36,
    label: 'Recovery line',
    run: ({ bullets }) => {
      bullets.spawnRing(0, 19, 12, 3.2, Math.PI / 12);
    },
  },
  {
    time: 43,
    label: 'Base perimeter',
    run: ({ enemies, playerX, playerY }) => {
      for (let index = 0; index < 10; index += 1) {
        enemies.spawn(EnemyRole.Tick, -14 + index * 3.1, 27 + (index % 3) * 0.8);
      }
      enemies.spawn(EnemyRole.Picket, -17, 26, playerX - 4, playerY);
      enemies.spawn(EnemyRole.Picket, 17, 26, playerX + 4, playerY);
    },
  },
  {
    time: 52,
    label: 'Lance and flock confirmation',
    run: ({ enemies }) => {
      enemies.spawn(EnemyRole.Roundel, -12, 28);
      enemies.spawn(EnemyRole.Roundel, 12, 28);
      for (let index = 0; index < 14; index += 1) {
        enemies.spawn(EnemyRole.Tick, -7.5 + index * 3, 29 + index * 0.45);
      }
    },
  },
  {
    time: 61,
    label: 'Quiet river — weapon recovery',
    run: () => {},
  },
  {
    time: 68,
    label: 'Second flock — tighter gap',
    run: ({ enemies }) => {
      for (let index = 0; index < 11; index += 1) {
        const x = -15 + index * 3;
        if (Math.abs(x) < 2.5) continue;
        enemies.spawn(EnemyRole.Tick, x, 27 + Math.abs(index - 5) * 0.42);
      }
    },
  },
  {
    time: 78,
    label: 'Crossing PICKET pair',
    run: ({ enemies, playerX, playerY }) => {
      for (let index = 0; index < 4; index += 1) {
        enemies.spawn(
          EnemyRole.Picket,
          index % 2 === 0 ? -17 : 17,
          25 + index,
          playerX + (index - 1.5) * 2.2,
          playerY,
        );
      }
    },
  },
  {
    time: 54,
    label: 'Riverine support on station',
    run: ({ allies }) => {
      // The James runs diagonally through the stage — a gunboat comes up it.
      allies?.deploy('boat', -4.5, -26);
    },
  },
  {
    time: 62,
    label: 'SHRIKE curtain — read the authored gap',
    run: ({ enemies }) => {
      // 8-wide lattice, slot 3 deliberately empty. That hole is the answer.
      for (let index = 0; index < 8; index += 1) {
        if (index === 3) continue;
        enemies.spawn(EnemyRole.Shrike, -14 + index * 4.0, 27);
      }
    },
  },
  {
    time: 70,
    label: 'GND-01 GUARD armour on the ground',
    run: ({ enemies }) => {
      enemies.spawn(EnemyRole.Guard, -8, 27);
      enemies.spawn(EnemyRole.Guard, 7, 30);
    },
  },
  {
    time: 78,
    label: 'SPLINTER — short-fuse divers',
    run: ({ enemies, playerX, playerY }) => {
      for (let index = 0; index < 5; index += 1) {
        enemies.spawn(EnemyRole.Splinter, -10 + index * 5, 27, playerX, playerY);
      }
    },
  },
  {
    time: 88,
    label: 'Guard installation',
    run: ({ enemies }) => {
      for (let index = 0; index < 5; index += 1) {
        enemies.spawn(EnemyRole.Roundel, -13 + index * 6.5, 27 + (index % 2) * 1.5);
      }
    },
  },
  {
    time: 94,
    label: 'CROSSTALK relay — kill it first',
    run: ({ enemies }) => {
      // The relay buffs everything in radius by 35% cadence. Leaving it alive
      // makes the whole wave faster — which is the lesson.
      enemies.spawn(EnemyRole.Crosstalk, 0, 28);
      for (let index = 0; index < 8; index += 1) {
        enemies.spawn(EnemyRole.Tick, -11 + index * 3.1, 30);
      }
    },
  },
  {
    time: 101,
    label: 'Black-tag pressure',
    run: ({ enemies }) => {
      for (let index = 0; index < 14; index += 1) {
        enemies.spawn(EnemyRole.Tick, -16 + index * 2.9, 27 + (index % 4) * 0.6);
      }
    },
  },
  {
    time: 107,
    label: 'AIR-01 KESTREL — manned interceptors',
    run: ({ enemies }) => {
      // Ours. Training-command red. The first thing you kill with a pilot in it.
      enemies.spawn(EnemyRole.Kestrel, -17, 24);
      enemies.spawn(EnemyRole.Kestrel, 17, 26);
      enemies.spawn(EnemyRole.Kestrel, -17, 29);
    },
  },
  {
    time: 112,
    label: 'Something behind the treeline',
    run: () => {},
  },
  // 116-122 — ECHO crosses at 68% of the 170 s pre-boss window.
  {
    time: 122,
    label: 'Base approach — Lance confirmation',
    run: ({ enemies }) => {
      for (let index = 0; index < 6; index += 1) {
        enemies.spawn(EnemyRole.Roundel, -12 + index * 4.8, 27 + (index % 3) * 1.2);
      }
    },
  },
  {
    time: 118,
    label: 'Second boat, starboard lane',
    run: ({ allies }) => {
      allies?.deploy('boat', 5.5, -26);
    },
  },
  {
    time: 122.5,
    label: 'DRN-05 DRAYMAN — kill the source',
    run: ({ enemies }) => {
      // The carrier births TICKs every 2.4 s until it dies. Ignore it and the
      // screen fills; kill it and the flock stops at the source.
      enemies.spawn(EnemyRole.Drayman, -6, 28);
    },
  },
  {
    time: 127,
    label: 'Curtain — read the authored gap',
    run: ({ enemies }) => {
      // 8-wide lattice; slot 3 is deliberately empty. That hole is the answer.
      for (let index = 0; index < 8; index += 1) {
        if (index === 3) continue;
        enemies.spawn(EnemyRole.Tick, -14 + index * 4.0, 27);
      }
    },
  },
  {
    time: 132,
    label: 'Guard armour on the approach',
    run: ({ enemies }) => {
      for (let index = 0; index < 3; index += 1) {
        enemies.spawn(EnemyRole.Roundel, -8 + index * 8, 27 + index * 1.4);
      }
    },
  },
  {
    time: 135,
    label: 'Second curtain — tighter',
    run: ({ enemies }) => {
      for (let index = 0; index < 10; index += 1) {
        if (index === 6) continue;
        enemies.spawn(EnemyRole.Tick, -15 + index * 3.2, 27 + (index % 2) * 0.8);
      }
    },
  },
  {
    time: 140,
    label: 'MIDBOSS — TWO VEHICLES',
    run: ({ enemies }) => {
      // Two heavies abreast, escorted. Recapitulates the first half.
      enemies.spawn(EnemyRole.Roundel, -5.5, 26);
      enemies.spawn(EnemyRole.Roundel, 5.5, 26);
      for (let index = 0; index < 8; index += 1) {
        enemies.spawn(EnemyRole.Tick, -14 + index * 4, 29 + (index % 3));
      }
    },
  },
  {
    time: 147,
    label: 'Combined arms — everything at once',
    run: ({ enemies, playerX, playerY }) => {
      enemies.spawn(EnemyRole.Crosstalk, 8, 29);
      enemies.spawn(EnemyRole.Guard, -10, 27);
      enemies.spawn(EnemyRole.GunPod, 12, 30);
      for (let index = 0; index < 4; index += 1) {
        enemies.spawn(EnemyRole.Splinter, -6 + index * 4, 31, playerX, playerY);
      }
    },
  },
  {
    time: 150,
    label: 'Midboss — escort wave',
    run: ({ enemies }) => {
      for (let index = 0; index < 10; index += 1) {
        enemies.spawn(EnemyRole.Picket, -13 + index * 2.9, 27 + (index % 4) * 1.1);
      }
    },
  },
  {
    time: 157,
    label: 'Pod recovery',
    run: ({ enemies }) => {
      for (let index = 0; index < 6; index += 1) {
        enemies.spawn(EnemyRole.Tick, -9 + index * 3.6, 27);
      }
    },
  },
  {
    time: 163,
    label: 'Base gate — silhouette under the pipework',
    run: () => {},
  },
  {
    time: 170,
    label: 'BATTERY NINE',
    run: () => {},
  },
];

export class StageDirector {
  private cursor = 0;
  private label = 'HOME FIELD — James River, 2090';
  private echoTriggered = false;

  update(
    elapsed: number,
    context: SpawnContext,
    environment: HomeFieldEnvironment,
  ): void {
    while (this.cursor < events.length && elapsed >= events[this.cursor].time) {
      const event = events[this.cursor];
      event.run(context);
      this.label = event.label;
      this.cursor += 1;
    }
    if (!this.echoTriggered && elapsed >= 116) {
      this.echoTriggered = true;
      this.label = 'ECHO // uncommanded—';
      environment.triggerEcho();
    }
  }

  getLabel(): string {
    return this.label;
  }

  getProgress(elapsed: number): number {
    return Math.min(1, elapsed / 170);
  }

  /** The boss gate. Stage action runs 170 s before BATTERY NINE (bible 03 §0.3). */
  shouldSpawnBoss(elapsed: number): boolean {
    return elapsed >= 170;
  }

  isComplete(_elapsed: number): boolean {
    // Completion is now owned by the boss, not the clock.
    return false;
  }

  /**
   * Fast-forward past every wave up to `elapsed` WITHOUT running them.
   * Used by the boss test state so jumping the clock does not dump 170 s of
   * spawns into a single frame.
   */
  skipTo(elapsed: number): void {
    while (this.cursor < events.length && elapsed >= events[this.cursor].time) {
      this.cursor += 1;
    }
    this.echoTriggered = elapsed >= 116;
    // Label the encounter actually being jumped to, not always the stage boss.
    this.label = elapsed >= 170 ? 'BATTERY NINE' : 'MIDBOSS — TWO VEHICLES';
  }

  reset(): void {
    this.cursor = 0;
    this.label = 'HOME FIELD — James River, 2090';
    this.echoTriggered = false;
  }
}
